import React from 'react';
import classNames from 'classnames';
import { useId } from '@floating-ui/react';

import { RCNonModalDialogBase, useRCNonModalDialogBaseContext } from '../internalComponents/RCNonModalDialogBase';

/**
 * Closes the `RCPopover` that contains the calling component.
 *
 * For content rendered inside a popover that should dismiss it once its work is done — a link that
 * navigates away, a form that submits, a "Done" button. Saves threading a close callback down
 * through the popover's children.
 *
 * Closing this way runs the same path as an outside-press or Escape, so the popover's
 * `onOpenChange` (and legacy `onDismiss`) still fire.
 *
 * Outside a popover, `close` is a no-op — safe in a component rendered both inside and outside one.
 *
 * ```tsx
 * function SaveAction() {
 *   const { close } = useRCPopover();
 *   return <RCButton onClick={() => { save(); close(); }}>Save</RCButton>;
 * }
 * ```
 */
export function useRCPopover() {
  return useRCNonModalDialogBaseContext();
}

type FloatingUIPositions = 'bottom' | 'bottom-start' | 'bottom-end' | 'right';

interface RCPopoverProps {
  /** Optional – Popovers manage their own state.
   * If you need to have state managed externally, use this in conjunction with onOpenChange  */
  isOpen?: boolean;

  /**
   * DEPRECATED – Use onOpenChange
   */
  onDismiss?: Function;

  /**
   * Optional – Open / Close event handler.
   * - (open = true) Can be used in place of an anchor onClick handler to track open events.
   * - (open = false) Should be used in place of onDismiss for handling close events.
   */
  onOpenChange?: (open: boolean) => void;

  /** *Desktop Only* - A formatted block of content that renders inside of the popover between the header and dismiss button*/
  children: React.ReactNode;

  /** The element to which the popover will point & anchor. */
  anchor: JSX.Element;

  /** The popover automatically positions itself,
   * but if you need to ensure it is always flush left or right (e.g. to avoid covering up key UI, use this. */
  position?: FloatingUIPositions;

  /** Decreases the opacity a bit. Use this when the contents inside the popover have some sort of async process
   * that occurs while the popover is still open. */
  loading?: boolean;

  /** "Trapping" focus keeps the focus tab order locked to contents inside the Popover.
   * When `true`, a user is required to dismiss the popover before they can tab to other elements on the page.
   * Change to `false` any time the user doesn't have a way to dismiss the popover with a keyboard.
   */
  trapFocus?: boolean;

  /** Use size to adjust the width of the popover. Large is a fixed 400px. Default is 240px - 350px, depending on content. */
  size?: 'default' | 'large';

  /** When sticky, a popover will remain on the screen even if its anchor has scrolled away.
   * By default, a popover will scroll with its anchor.
   */
  sticky?: boolean;

  /** Which element receives focus when the popover opens. Defaults to 0, the first tabbable
   * element in the content. Pass -1 to leave focus where it is - correct for a popover that
   * opens on hover, where moving focus paints a focus ring and scrolls the panel without the
   * user ever asking for it. Keyboard users still tab into the content either way.
   */
  initialFocus?: -1 | 0 | 1 | 2;

  /** Opens the popover on hover as well as on click. Mouse only. Hover delays come from an
   * enclosing `FloatingDelayGroup` when there is one.
   */
  openOnHover?: boolean;
}

/**
 * Our general Popover component. Auto-positioning and accessibility-enhanced. Use this for submenus, dropdowns, and the like.
 */
export function RCPopover({
  isOpen = undefined,
  onOpenChange = undefined,
  onDismiss = undefined,
  position = 'bottom-start',
  children,
  anchor,
  loading = false,
  trapFocus = false,
  size = 'default',
  sticky = false,
  initialFocus = 0,
  openOnHover = false,
}: RCPopoverProps) {
  const id = useId();

  return (
    <RCNonModalDialogBase
      id={id}
      isOpen={isOpen}
      onOpenChange={
        onOpenChange ? onOpenChange : (
          onDismiss ? () => onDismiss() : undefined
        )
      }
      position={position}
      anchor={anchor}
      trapFocus={trapFocus}
      variant={onDismiss ? 'legacy' : 'default'}
      sticky={sticky}
      initialFocus={initialFocus}
      openOnHover={openOnHover}
    >
      <div
        className={classNames(
          'rc-popover',
          { 'rc-popover--loading': loading },
          { 'rc-popover--large': size === 'large' },
        )}
      >
        <div className="rc-popover__content">
          {children}
        </div>
      </div>
    </RCNonModalDialogBase>
  );
}
